import { FC, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { EditIcon } from '../../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Switch } from '../../../../components/switch/index';
import { useFormat, useModalState } from '../../../../hooks/index';
import {
  SharePlanType,
  sharePlanTypeTitle,
  SharePlanVestingType,
  terminationIntervalTitle,
  VestingOnTitle,
} from '../../../../types/pool-plans.types';
import { openSelectedCompanyStorageUrlInNewTab } from '../../../../utils/company-storage-url.util';
import { toNumber } from '../../../../utils/to-number.util';
import { RecordAttachedFilesPreview } from '../../components/record-attached-files-preview';
import { TerminationModal } from '../../termination-modal';
import { ChildrenFormProps, EditPlanStepVariant } from '../plan-form.types';
import { FormSchema } from '../plan-form.validation';
import { formatRsaPurchasePrice, formatSingular } from './final-step';

export type EditPlanPreviewProps = {
  handleTerminate: (date: Date) => void;
  onEditClick: (step: EditPlanStepVariant) => void;
  previewMode?: true;
} & ChildrenFormProps;

export const EditPlanPreview: FC<EditPlanPreviewProps> = ({
  handleCloseModal,
  formData,
  control,
  onEditClick,
  handleTerminate,
  previewMode,
  isTerminated,
  terminationDate,
  docusignFileLinks,
}) => {
  const { format } = useFormat();
  useWatch<FormSchema>({ control });
  const [terminatingData, setTerminatingData] = useState<{
    date: Date | null;
    isTerminating: boolean;
  } | null>(null);
  const { stepOne, stepTwo, stepThree, stepFour } = formData();

  const handleDocumentClick = (link: string) => {
    void openSelectedCompanyStorageUrlInNewTab(link);
  };

  const {
    toggler: terminationModalToggler,
    isOpen: isOpenTerminationModal,
    handleOpenModal: handleOpenTerminationModal,
    handleCloseModal: handleCloseTerminationModal,
    handleSuccessModal: handleSuccessTerminationModalModal,
  } = useModalState({
    onSuccess: () => {
      if (!terminatingData?.date) return;
      handleTerminate(terminatingData.date);
      handleCloseModal();
    },
  });

  return (
    <>
      <AlertDialogWrapper
        control={{ onOpenChange: terminationModalToggler, open: isOpenTerminationModal }}
      >
        <TerminationModal
          onClose={handleCloseTerminationModal}
          onSuccess={handleSuccessTerminationModalModal}
          text="plan"
        />
      </AlertDialogWrapper>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <div className="flex w-full justify-between px-4">
            <span className="text-sm font-[550] text-gray-700">
              <AppFormattedMessage id="GENERAL" />
            </span>
            {!previewMode && (
              <Button className="w-fit" onClick={() => onEditClick(1)} styleType="NONE">
                <EditIcon iconColor="#2565C8" />
              </Button>
            )}
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-0">
            <SummaryItem
              className="pt-0"
              title={<AppFormattedMessage id="PLAN_NAME" />}
              value={stepOne?.name}
            />
            <SummaryItem title="Pool" value={stepOne?.pool?.name} />
            <SummaryItem
              title={<AppFormattedMessage id="DATE" />}
              value={stepOne?.date ? format(stepOne?.date, 'dd/MM/yyyy') : ''}
            />
            <SummaryItem
              title={<AppFormattedMessage id="EQUITY_PLAN_TYPE" />}
              value={
                <AppFormattedMessage
                  id={stepOne?.type ? sharePlanTypeTitle[stepOne?.type] : 'EMPTY'}
                />
              }
            />
            {stepOne?.type === SharePlanType.GROWTH_HURDLE && (
              <SummaryItem
                title={<AppFormattedMessage id="HURDLE_VALUE" />}
                value={
                  stepOne?.financialDetails['growth-hurdle']?.value &&
                  toNumber(stepOne?.financialDetails['growth-hurdle']?.value)?.toLocaleString(
                    'en-US',
                  )
                }
              />
            )}
            {stepOne?.type === SharePlanType.STOCK_OPTIONS && (
              <>
                <SummaryItem
                  title={<AppFormattedMessage id="STRIKE_PRICE_VALUE" />}
                  value={toNumber(
                    stepOne?.financialDetails['stock-options']?.value,
                  )?.toLocaleString('en-US')}
                />
                {/* <SummaryItem
                  title={<AppFormattedMessage id="CONVERSION_RATIO_X" />}
                  value={stepOne?.financialDetails['stock-options']?.conversionRatio || '-'}
                /> */}
              </>
            )}
            {stepOne?.type === SharePlanType.BSPCE && (
              <>
                <SummaryItem
                  title={<AppFormattedMessage id="STRIKE_PRICE_VALUE" />}
                  value={toNumber(stepOne?.financialDetails['bspce']?.value)?.toLocaleString(
                    'en-US',
                  )}
                />
                {/* <SummaryItem
                  title={<AppFormattedMessage id="CONVERSION_RATIO_X" />}
                  value={stepOne?.financialDetails['bspce']?.conversionRatio || '-'}
                /> */}
              </>
            )}
            {stepOne?.type === SharePlanType.WARRANTS && (
              <>
                <SummaryItem
                  title={<AppFormattedMessage id="WARRANT_PRICE_VALUE" />}
                  value={toNumber(stepOne?.financialDetails['warrants']?.value)?.toLocaleString(
                    'en-US',
                  )}
                />
                {/* <SummaryItem
                  title={<AppFormattedMessage id="CONVERSION_RATIO_X" />}
                  value={stepOne?.financialDetails['warrants']?.conversionRatio || '-'}
                /> */}
              </>
            )}
            {stepOne?.type === SharePlanType.SAR && (
              <SummaryItem
                title={<AppFormattedMessage id="SAR_BASE_PRICE" />}
                value={toNumber(stepOne?.financialDetails['sar']?.value)?.toLocaleString('en-US')}
              />
            )}
            {/* {stepOne?.type === SharePlanType.RSU && (
              <SummaryItem
                title={<AppFormattedMessage id="CONVERSION_RATIO_X" />}
                value={stepOne?.financialDetails['rsu']?.conversionRatio || '-'}
              />
            )} */}
            {/* {stepOne?.type === SharePlanType.PHANTOM && (
              <SummaryItem
                title={<AppFormattedMessage id="CONVERSION_RATIO_X" />}
                value={stepOne?.financialDetails['phantom']?.conversionRatio || '-'}
              />
            )} */}
            {stepOne?.type === SharePlanType.RSA && (
              <>
                <SummaryItem
                  title={<AppFormattedMessage id="RSA_PURCHASE_PRICE" />}
                  value={formatRsaPurchasePrice(stepOne?.financialDetails['rsa']?.value)}
                />
                {/* <SummaryItem
                  title={<AppFormattedMessage id="CONVERSION_RATIO_X" />}
                  value={stepOne?.financialDetails['rsa']?.conversionRatio || '-'}
                /> */}
              </>
            )}
          </div>
        </div>
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <div className="flex w-full justify-between px-4">
            <span className="text-sm font-[550] text-gray-700">
              <AppFormattedMessage id="VESTING" />
            </span>
            {!previewMode && (
              <Button className="w-fit" onClick={() => onEditClick(2)} styleType="NONE">
                <EditIcon iconColor="#2565C8" />
              </Button>
            )}
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            {!stepTwo?.vesting && (
              <span
                className={twMerge(
                  'text-xs font-[450] text-gray-500',
                  stepOne?.type === SharePlanType.WARRANTS && 'pb-3',
                )}
              >
                <AppFormattedMessage id="NO_VESTING_STRUCTURE_SELECTED" />
              </span>
            )}
            {stepTwo?.vesting?.vestingMechanism === SharePlanVestingType.TIME_BASED_STANDARD && (
              <>
                <div>
                  <SummaryItem
                    className="pt-0"
                    title={<AppFormattedMessage id="UPDATE" />}
                    value={<AppFormattedMessage id="TIME_BASED_STANDARD" />}
                  />
                  <SummaryItem
                    className="pt-0"
                    title={<AppFormattedMessage id="VESTING_ON" />}
                    value={
                      stepTwo?.vesting?.timeBasedStandard?.vestingOn && (
                        <AppFormattedMessage
                          id={VestingOnTitle[stepTwo?.vesting?.timeBasedStandard?.vestingOn]}
                        />
                      )
                    }
                  />
                </div>
                <div>
                  <SummaryItem
                    className="pb-0"
                    title={<AppFormattedMessage id="DURATION" />}
                    value={formatSingular(
                      stepTwo?.vesting?.timeBasedStandard?.vestingDuration,
                      'month',
                    )}
                  />
                  <SummaryItem
                    className="pb-0"
                    title={<AppFormattedMessage id="VESTING_FREQUENCY_ABBR" />}
                    value={formatSingular(
                      stepTwo?.vesting?.timeBasedStandard?.vestingFrequency,
                      'month',
                    )}
                  />
                  <SummaryItem
                    className="pb-0"
                    title={<AppFormattedMessage id="CLIFF" />}
                    value={formatSingular(
                      stepTwo?.vesting?.timeBasedStandard?.cliffPeriod,
                      'month',
                    )}
                  />
                  <SummaryItem
                    className={stepTwo?.expiryDate?.enabled ? 'pb-3' : 'pb-0'}
                    title={<AppFormattedMessage id="ACCELERATION_DATE" />}
                    value={
                      stepTwo?.vesting?.timeBasedStandard?.accelerationDate
                        ? format(
                            stepTwo?.vesting?.timeBasedStandard?.accelerationDate,
                            'dd/MM/yyyy',
                          )
                        : '-'
                    }
                  />
                </div>
              </>
            )}
            {stepTwo?.vesting?.vestingMechanism === SharePlanVestingType.TIME_BASED_DYNAMIC && (
              <>
                <div>
                  <SummaryItem
                    className="pt-0"
                    title={<AppFormattedMessage id="TYPE" />}
                    value={<AppFormattedMessage id="TIME_BASED_DYNAMIC" />}
                  />
                  <SummaryItem
                    className="pt-0"
                    title={<AppFormattedMessage id="VESTING_ON" />}
                    value={
                      stepTwo?.vesting?.timeBasedDynamic?.vestingOn && (
                        <AppFormattedMessage
                          id={VestingOnTitle[stepTwo?.vesting?.timeBasedDynamic?.vestingOn]}
                        />
                      )
                    }
                  />
                </div>

                <div className="flex flex-col divide-y divide-gray-100">
                  {stepTwo?.vesting?.timeBasedDynamic?.dynamicVestings.map((period, index) => (
                    <div className="pt-3" key={index}>
                      <span className="text-xs font-[550] text-gray-700">
                        <AppFormattedMessage id="PERIOD" /> {index + 1}
                      </span>
                      <SummaryItem
                        className="pb-0"
                        title={<AppFormattedMessage id="DURATION" />}
                        value={formatSingular(period.vestingDuration, 'month')}
                      />
                      <SummaryItem
                        className="pb-0"
                        title={<AppFormattedMessage id="VESTING_FREQUENCY_ABBR" />}
                        value={formatSingular(period.vestingFrequency, 'month')}
                      />
                      <SummaryItem
                        title={<AppFormattedMessage id="PLAN_ALLOCATION" />}
                        value={`${period.planAllocation} %`}
                      />
                    </div>
                  ))}
                </div>
                <SummaryItem
                  className={stepTwo?.expiryDate?.enabled && 'pb-3'}
                  title={<AppFormattedMessage id="ACCELERATION_DATE" />}
                  value={
                    stepTwo?.vesting?.timeBasedDynamic?.accelerationDate
                      ? format(stepTwo?.vesting?.timeBasedDynamic?.accelerationDate, 'dd/MM/yyyy')
                      : '-'
                  }
                />
              </>
            )}
            {stepTwo?.vesting?.vestingMechanism === SharePlanVestingType.TARGET_BASED && (
              <>
                <SummaryItem
                  className="pt-0"
                  title={<AppFormattedMessage id="TYPE" />}
                  value={<AppFormattedMessage id="TARGET_BASED" />}
                />
                {stepTwo?.vesting?.targetBasedStandard?.targetVestings.map((period, index) => (
                  <div className="pt-3" key={index}>
                    <span className="text-xs font-[550] text-gray-700">
                      <AppFormattedMessage id="MILESTONE" /> {index + 1}
                    </span>
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="MILESTONE_NAME" />}
                      value={period.name}
                    />
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="WEIGHT" />}
                      value={`${Math.round(period.weight)} %`}
                    />
                    <SummaryItem
                      title={<AppFormattedMessage id="TARGET_DATE" />}
                      value={period.targetDate ? format(period.targetDate, 'dd/MM/yyyy') : '-'}
                    />
                  </div>
                ))}
              </>
            )}
            {stepOne?.type === SharePlanType.WARRANTS && (
              <SummaryItem
                className="pb-0"
                title={<AppFormattedMessage id="EXPIRY_DATE" />}
                value={
                  stepTwo?.expiryDate?.date ? format(stepTwo?.expiryDate?.date, 'dd/MM/yyyy') : '-'
                }
              />
            )}
          </div>
        </div>

        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <div className="flex w-full justify-between px-4">
            <span className="text-sm font-[550] text-gray-700">
              <AppFormattedMessage id="TERMINATION_LOGIC" />
            </span>

            {!previewMode && (
              <Button className="w-fit" onClick={() => onEditClick(3)} styleType="NONE">
                <EditIcon iconColor="#2565C8" />
              </Button>
            )}
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            {!stepThree?.terminationLogic && (
              <span className="text-xs font-[450] text-gray-500">
                <AppFormattedMessage id="NO_TERMINATION_LOGIC_SELECTED" />
              </span>
            )}
            {stepThree?.terminationLogic?.enabled && (
              <>
                {stepThree?.terminationLogic?.resignation?.enabled ? (
                  <div>
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="RESIGNATION" />}
                      value={<AppFormattedMessage id="EXERCISE_EXPIRATION_PERIOD" />}
                    />
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="VALUE" />}
                      value={stepThree.terminationLogic.resignation.value}
                    />
                    <SummaryItem
                      title={<AppFormattedMessage id="INTERVAL" />}
                      value={
                        <AppFormattedMessage
                          id={
                            terminationIntervalTitle[
                              stepThree.terminationLogic.resignation.interval
                            ]
                          }
                        />
                      }
                    />
                  </div>
                ) : (
                  <SummaryItem
                    title={<AppFormattedMessage id="RESIGNATION" />}
                    value={<AppFormattedMessage id="NO_EXERCISE_EXPIRATION_PERIOD" />}
                  />
                )}
                {stepThree?.terminationLogic?.termination?.enabled ? (
                  <div>
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="TERMINATION" />}
                      value={<AppFormattedMessage id="EXERCISE_EXPIRATION_PERIOD" />}
                    />
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="VALUE" />}
                      value={stepThree.terminationLogic.termination.value}
                    />
                    <SummaryItem
                      title={<AppFormattedMessage id="INTERVAL" />}
                      value={
                        <AppFormattedMessage
                          id={
                            terminationIntervalTitle[
                              stepThree.terminationLogic.termination.interval
                            ]
                          }
                        />
                      }
                    />
                  </div>
                ) : (
                  <SummaryItem
                    title={<AppFormattedMessage id="TERMINATION" />}
                    value={<AppFormattedMessage id="NO_EXERCISE_EXPIRATION_PERIOD" />}
                  />
                )}
                {stepThree?.terminationLogic?.terminationWithCause?.enabled ? (
                  <div>
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="TERMINATION_WITH_CAUSE" />}
                      value={<AppFormattedMessage id="EXERCISE_EXPIRATION_PERIOD" />}
                    />
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="VALUE" />}
                      value={stepThree.terminationLogic.terminationWithCause.value}
                    />
                    <SummaryItem
                      title={<AppFormattedMessage id="INTERVAL" />}
                      value={
                        <AppFormattedMessage
                          id={
                            terminationIntervalTitle[
                              stepThree.terminationLogic.terminationWithCause.interval
                            ]
                          }
                        />
                      }
                    />
                  </div>
                ) : (
                  <SummaryItem
                    title={<AppFormattedMessage id="TERMINATION_WITH_CAUSE" />}
                    value={<AppFormattedMessage id="NO_EXERCISE_EXPIRATION_PERIOD" />}
                  />
                )}
                {stepThree?.terminationLogic?.retirement?.enabled ? (
                  <div>
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="RETIREMENT" />}
                      value={<AppFormattedMessage id="EXERCISE_EXPIRATION_PERIOD" />}
                    />
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="VALUE" />}
                      value={stepThree.terminationLogic.retirement.value}
                    />
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="INTERVAL" />}
                      value={
                        <AppFormattedMessage
                          id={
                            terminationIntervalTitle[stepThree.terminationLogic.retirement.interval]
                          }
                        />
                      }
                    />
                  </div>
                ) : (
                  <SummaryItem
                    className="pb-0"
                    title={<AppFormattedMessage id="RETIREMENT" />}
                    value={<AppFormattedMessage id="NO_EXERCISE_EXPIRATION_PERIOD" />}
                  />
                )}
                {stepThree?.terminationLogic?.death?.enabled ? (
                  <div>
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="DEATH" />}
                      value={<AppFormattedMessage id="EXERCISE_EXPIRATION_PERIOD" />}
                    />
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="VALUE" />}
                      value={stepThree.terminationLogic.death.value}
                    />
                    <SummaryItem
                      title={<AppFormattedMessage id="INTERVAL" />}
                      value={
                        <AppFormattedMessage
                          id={terminationIntervalTitle[stepThree.terminationLogic.death.interval]}
                        />
                      }
                    />
                  </div>
                ) : (
                  <SummaryItem
                    className="pb-0"
                    title={<AppFormattedMessage id="DEATH" />}
                    value={<AppFormattedMessage id="NO_EXERCISE_EXPIRATION_PERIOD" />}
                  />
                )}
                {stepThree?.terminationLogic?.disability?.enabled ? (
                  <div>
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="DISABILITY" />}
                      value={<AppFormattedMessage id="EXERCISE_EXPIRATION_PERIOD" />}
                    />
                    <SummaryItem
                      className="pb-0"
                      title={<AppFormattedMessage id="VALUE" />}
                      value={stepThree.terminationLogic.disability.value}
                    />
                    <SummaryItem
                      title={<AppFormattedMessage id="INTERVAL" />}
                      value={
                        <AppFormattedMessage
                          id={
                            terminationIntervalTitle[stepThree.terminationLogic.disability.interval]
                          }
                        />
                      }
                    />
                  </div>
                ) : (
                  <SummaryItem
                    className="pb-0"
                    title={<AppFormattedMessage id="DISABILITY" />}
                    value={<AppFormattedMessage id="NO_EXERCISE_EXPIRATION_PERIOD" />}
                  />
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex w-full items-start justify-between rounded-lg border border-gray-100 bg-white px-4">
          {(docusignFileLinks?.length ?? 0) > 0 ? (
            <RecordAttachedFilesPreview
              docusignFileLinks={docusignFileLinks}
              files={stepFour?.files}
              onDocumentClick={handleDocumentClick}
              previewMode={previewMode}
            />
          ) : (
            <div className={'flex min-w-0 flex-1 gap-3 py-2'}>
              <span className="w-[120px] shrink-0 text-xs font-[450] text-gray-500">
                <AppFormattedMessage id="DOCUMENTS" />:
              </span>
              <RecordAttachedFilesPreview
                files={stepFour?.files}
                onDocumentClick={handleDocumentClick}
                previewMode={previewMode}
              />
            </div>
          )}
          {!previewMode && (
            <Button className="w-fit shrink-0 pt-2" onClick={() => onEditClick(4)} styleType="NONE">
              <EditIcon iconColor="#2565C8" />
            </Button>
          )}
        </div>
        <div className="flex w-full items-start justify-between rounded-lg border border-gray-100 bg-white px-4 py-2">
          <div className="flex w-full gap-3">
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
            </span>
            <div
              className={twMerge(
                'max-w-[250px] break-words text-xs font-[450] text-gray-700',
                !previewMode ? 'max-w-[220px]' : 'max-w-[250px]',
              )}
            >
              {stepFour?.additionalNotes || '-'}
            </div>
          </div>
          {!previewMode && (
            <Button className="w-fit" onClick={() => onEditClick(4)} styleType="NONE">
              <EditIcon iconColor="#2565C8" />
            </Button>
          )}
        </div>

        {!previewMode && !isTerminated && (
          <div className="flex w-full flex-col justify-center rounded-lg bg-fireside-25 px-1 py-1">
            <div className="flex gap-2 px-3 py-3">
              <Switch
                onChange={(state) =>
                  setTerminatingData(state ? { isTerminating: state, date: null } : null)
                }
              />
              <span className="text-sm font-[550] text-fireside-600">
                <AppFormattedMessage id="TERMINATE_PLAN" />
              </span>
            </div>
            {terminatingData && (
              <div className="flex w-full flex-col gap-3 rounded-md bg-white p-3 shadow-xs">
                <FormDatePicker
                  calendar={{ fromDate: new Date() }}
                  inputValue={
                    terminatingData?.date ? format(terminatingData?.date, 'dd/MM/yy') : ''
                  }
                  inputWrapperClassName="cursor-pointer"
                  onSelect={(date) => setTerminatingData({ isTerminating: true, date })}
                  placeholder={<AppFormattedMessage id="DATE_OF_TERMINATION" />}
                  value={terminatingData?.date}
                />
                <DropDown
                  answer="Plan termination refers to the process of ending an employee stock option plan, stock purchase plan, or any other equity compensation plan."
                  className="rounded-lg bg-gray-50"
                  question={<AppFormattedMessage id="PLAN_TERMINATION_WORKS" />}
                />
              </div>
            )}
          </div>
        )}

        {!previewMode && isTerminated && terminationDate && (
          <div className="flex flex-col gap-3 p-3 pt-5">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-[550] text-fireside-600">
                <AppFormattedMessage id="TERMINATE_PLAN" />
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-[450] text-gray-700">
                  <AppFormattedMessage id="CURRENT_TERMINATION_DATE" />:
                </span>
                <span className="text-xs font-[450] text-fireside-600">
                  {terminationDate ? format(terminationDate, 'dd/MM/yy') : ''}
                </span>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 rounded-md bg-white p-3 shadow-xs">
              <FormDatePicker
                calendar={{ fromDate: new Date() }}
                inputValue={terminatingData?.date ? format(terminatingData.date, 'dd/MM/yy') : ''}
                inputWrapperClassName="cursor-pointer"
                onSelect={(date) => setTerminatingData({ isTerminating: true, date })}
                placeholder={<AppFormattedMessage id="NEW_TERMINATION_DATE" />}
                value={terminatingData?.date}
              />
              <DropDown
                answer="Plan termination refers to the process of ending an employee stock option plan, stock purchase plan, or any other equity compensation plan."
                className="rounded-lg bg-gray-50"
                question={<AppFormattedMessage id="PLAN_TERMINATION_WORKS" />}
              />
            </div>
          </div>
        )}
      </div>

      <div
        className={twMerge(
          'flex h-9 w-full justify-start gap-3 px-6',
          (previewMode || terminatingData) && 'justify-end',
        )}
      >
        <Button
          className={twMerge(
            'w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700',
            terminatingData && 'bg-fireside-600 text-white',
            previewMode && 'bg-brand-700 text-white',
          )}
          disabled={(terminatingData && !terminatingData.date) || false}
          onClick={() => {
            if (!terminatingData) return handleCloseModal();

            terminatingData.date && handleOpenTerminationModal();
          }}
          styleType="NONE"
          type="button"
        >
          {terminatingData ? (
            <AppFormattedMessage id="TERMINATE_PLAN" />
          ) : previewMode ? (
            <AppFormattedMessage id="CLOSE" />
          ) : (
            <AppFormattedMessage id="CANCEL" />
          )}
        </Button>
      </div>
    </>
  );
};
